import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import colors from "../../../Constants/colors";
import { mainContainer } from "./GroupManagementStyles";
import { useDispatch, useSelector } from "react-redux";
import { Add, DeleteOutline, ExpandMore } from "@mui/icons-material";
import {
  addMultipleBotsToGroup,
  addMultipleUsersToGroup,
  addUserToGroup,
  createOrgGroup,
  deleteBotFromGroup,
  deleteOrgGroup,
  deleteUserFromGroup,
  getAllUsers,
  getGroupsByOrg,
} from "../../../features/organization/orgSlice";
import { getUserByID } from "../../../features/auth/authSlice";
import {
  getBotbyID,
  getBotbyOrg,
} from "../../../features/chatbots/chatbotSlice";
import {
  deleteIconStyles,
  iconButtonActive,
  primaryButtonStyles,
} from "../../../Constants/buttonStyles";
import { tableStyles } from "../../../Constants/tableStyles";
import theme, { scrollbarStyles } from "../../../Constants/theme";
import { accordionStyles } from "../../../Constants/accordionStyles";

import CreateGroupDialog from "./ManagementComponents/Groups/CreateGroupDialog";
import AddBotsToGroup from "./ManagementComponents/Groups/AddBotsToGroup";
import DeleteConfirmDailog from "./ManagementComponents/Groups/DeleteConfirmDailog";
import AddUsersToGroup from "./ManagementComponents/Groups/AddUsersToGroup";

const GroupManagement = () => {
  const [orgGroups, setOrgGroups] = useState([]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [usersData, setUsersData] = useState({});
  const [botsData, setBotsData] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingBots, setLoadingBots] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCreateGroup, setLoadingCreateGroup] = useState(false);
  const { groups, organization } = useSelector((state) => state.organization);
  const { chatbots } = useSelector((state) => state.chatbot);
  const { user } = useSelector((state) => state.auth);

  // create new group usestates
  const [open, setOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupNameError, setGroupNameError] = useState(false);

  // delete group usestates
  const [openConfirmDeleteGroupDialog, setOpenConfirmDeleteGroupDialog] =
    useState(false);
  const [selectedGroupDelete, setSelectedGroupDelete] = useState(null);

  // State for add bots to group Dialog
  const [openBotsDialog, setOpenBotsDialog] = useState(false);
  const [botsList, setBotsList] = useState([]);
  const [selectedBots, setSelectedBots] = useState([]);
  const [selectedGroupForBots, setSelectedGroupForBots] = useState(null);

  // delte bots from group usestates
  const [openConfirmDeleteBotDialog, setOpenConfirmDeleteBotDialog] =
    useState(false);
  const [selectedBotToDelete, setSelectedBotToDelete] = useState(null);
  const [selectedGroupForDelete, setSelectedGroupForDelete] = useState(null);

  // State for Add Users to Group Dialog
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroupForUsers, setSelectedGroupForUsers] = useState(null);
  const [loadingUsersDialog, setLoadingUsersDialog] = useState(false);

  // State for deleting users
  const [openConfirmDeleteUserDialog, setOpenConfirmDeleteUserDialog] =
    useState(false);
  const [selectedUserToDelete, setSelectedUserToDelete] = useState(null);
  const [selectedGroupForUserDelete, setSelectedGroupForUserDelete] =
    useState(null);

  const dispatch = useDispatch();
  const org_id = organization?._id["$oid"];

  // Fetch Groups when Component Loads
  useEffect(() => {
    if (!org_id) return;

    const fetchGroups = async () => {
      setLoading(true);
      try {
        await dispatch(getGroupsByOrg({ org_id })).unwrap();
      } catch (error) {
        console.error("Error fetching groups:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [org_id, dispatch]);

  useEffect(() => {
    if (user?.user?.privilege === "owner") {
      // Owners can view all groups
      setOrgGroups(groups);
    } else {
      // Non-owners can only view groups they are part of
      const userGroupIds = user?.user?.groups?.map((group) => group.$oid) || [];
      const filteredGroups = groups.filter((group) =>
        userGroupIds.includes(group._id.$oid)
      );
      setOrgGroups(filteredGroups);
    }
  }, [groups, dispatch]);

  // Handle Accordion Expansion
  const handleAccordionChange = async (groupId, users, bots) => {
    const isExpanding = expandedGroup !== groupId;
    setExpandedGroup(isExpanding ? groupId : null);

    if (isExpanding) {
      setLoadingUsers(true);
      setLoadingBots(true);

      try {
        const userRequests = users.map((userId) => {
          const validUserId = userId?.$oid; // Ensure valid ID
          return dispatch(getUserByID(validUserId)).unwrap(); // Return the promise
        });

        const botRequests = bots.map((botId) => {
          const validBotId = botId?.$oid;
          return dispatch(getBotbyID({ bot_id: validBotId })).unwrap(); //Return the promise
        });

        const userResponses = await Promise.all(userRequests);
        const botResponses = await Promise.all(botRequests);

        // Store fetched users
        const updatedUsersData = {};
        userResponses.forEach((response) => {
          const user = response?.user; // Extract user from response
          if (user) {
            updatedUsersData[user._id.$oid] = {
              id: user._id.$oid,
              firstName: user.first_name,
              lastName: user.last_name,
              email: user.email,
              privilege: user.privilege,
            };
          }
        });

        // Store fetched bots
        const updatedBotsData = {};
        botResponses.forEach((response) => {
          const bot = response?.bot;
          if (bot) {
            updatedBotsData[bot._id.$oid] = {
              id: bot._id.$oid,
              name: bot.name,
              createdOn: bot.created_at,
            };
          }
        });

        setUsersData((prev) => ({ ...prev, ...updatedUsersData }));
        setBotsData((prev) => ({ ...prev, ...updatedBotsData }));
      } catch (error) {
        console.error("Error fetching users or bots:", error);
      } finally {
        setLoadingUsers(false);
        setLoadingBots(false);
      }
    }
  };

  // create new group functions
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setGroupName("");
    setOpen(false);
  };
  const handleCreateGroup = () => {
    if (!groupName.trim()) return;

    setLoadingCreateGroup(true);
    const newGroupData = {
      org_id,
      name: groupName,
      owner: organization?.owner,
    };

    dispatch(createOrgGroup(newGroupData))
      .unwrap()
      .then(() => {
        setLoadingCreateGroup(false);
        handleClose();
      })
      .catch((error) => {
        console.error("Error creating group:", error);
        setLoadingCreateGroup(false);
      });
  };

  const handleGroupNameChange = (e) => {
    const newName = e.target.value;
    setGroupName(newName);

    // Check for duplicates
    const isDuplicate = orgGroups.some(
      (group) => group.name.toLowerCase() === newName.trim().toLowerCase()
    );
    setGroupNameError(isDuplicate);
  };

  // delete group functions
  const handleOpenConfirmDeleteGroupDialog = (groupId) => {
    setSelectedGroupDelete(groupId);
    setOpenConfirmDeleteGroupDialog(true);
  };

  const handleCloseDeleteGroupDialog = () => {
    setSelectedGroupDelete(null);
    setOpenConfirmDeleteGroupDialog(false);
  };

  const handleConfirmDeleteGroup = () => {
    if (selectedGroupDelete) {
      dispatch(deleteOrgGroup(selectedGroupDelete))
        .unwrap()
        .catch((error) => console.error("Error deleting group:", error));
    }
    handleCloseDeleteGroupDialog();
  };

  // add bots to group functions
  const handleOpenBotsDialog = async (groupId, existingBots) => {
    setSelectedGroupForBots(groupId); // Store the selected group ID

    if (!organization?.bot?.length) return;

    if (
      chatbots?.combinations &&
      chatbots.combinations.length === organization.bot.length
    ) {
      // Exclude bots that are already in the group
      const filteredBots = chatbots.combinations.filter(
        (bot) => !existingBots.includes(bot._id.$oid)
      );
      setBotsList(filteredBots);
      setOpenBotsDialog(true);
    } else {
      setLoadingBots(true);
      try {
        const response = await dispatch(getBotbyOrg(org_id)).unwrap();
        if (response?.bots) {
          // Exclude bots that are already in the group
          const filteredBots = response.bots.filter(
            (bot) => !existingBots.includes(bot._id.$oid)
          );
          setBotsList(filteredBots);
          setOpenBotsDialog(true);
        }
      } catch (error) {
        console.error("Error fetching bots:", error);
      } finally {
        setLoadingBots(false);
      }
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (botId) => {
    setSelectedBots((prev) =>
      prev.includes(botId)
        ? prev.filter((id) => id !== botId)
        : [...prev, botId]
    );
  };

  // Handle adding bots to group
  const handleAddBots = async () => {
    if (selectedBots.length === 0 || !selectedGroupForBots) return;

    try {
      setOpenBotsDialog(false);
      // Optimistic UI Update: Add "Loading..." placeholders
      const newBots = selectedBots.map((botId) => ({
        id: botId,
        name: "Loading...",
      }));

      // Update botsData optimistically
      setBotsData((prev) => {
        const updatedBotsData = { ...prev };
        newBots.forEach((bot) => {
          updatedBotsData[bot.id] = bot;
        });
        return updatedBotsData;
      });

      // Update orgGroups UI optimistically
      setOrgGroups((prevGroups) =>
        prevGroups.map((group) =>
          group._id.$oid === selectedGroupForBots
            ? {
                ...group,
                bots: [...group.bots, ...newBots.map((b) => ({ $oid: b.id }))],
              }
            : group
        )
      );

      // Dispatch bulk API request to add bots
      await dispatch(
        addMultipleBotsToGroup({
          group_id: selectedGroupForBots,
          bot_id_list: selectedBots,
        })
      ).unwrap();

      // Fetch actual bot details after adding
      const botRequests = selectedBots.map((botId) =>
        dispatch(getBotbyID({ bot_id: botId })).unwrap()
      );
      const botResponses = await Promise.all(botRequests);

      // Update state with actual bot details
      const updatedBots = {};
      botResponses.forEach((response) => {
        if (response?.bot) {
          const bot = response.bot;
          updatedBots[bot._id.$oid] = {
            id: bot._id.$oid,
            name: bot.name,
            createdOn: bot.created_at,
          };
        }
      });

      setBotsData((prev) => ({ ...prev, ...updatedBots }));

      // Ensure orgGroups is updated with fetched bot details
      setOrgGroups((prevGroups) =>
        prevGroups.map((group) =>
          group._id.$oid === selectedGroupForBots
            ? {
                ...group,
                bots: [
                  ...group.bots.filter((b) => !selectedBots.includes(b.$oid)),
                  ...selectedBots.map((botId) => ({ $oid: botId })),
                ],
              }
            : group
        )
      );

      // Close dialog and reset selections
      setOpenBotsDialog(false);
      setSelectedGroupForBots(null);
      setSelectedBots([]);
    } catch (error) {
      console.error("Error adding bots to group:", error);
    }
  };

  // delete bots from group confirm dialog
  // Open delete bot dialog
  const handleOpenConfirmDeleteBotDialog = (groupId, botId) => {
    setSelectedGroupForDelete(groupId);
    setSelectedBotToDelete(botId);
    setOpenConfirmDeleteBotDialog(true);
  };

  // Close delete bot dialog
  const handleCloseDeleteBotDialog = () => {
    setSelectedBotToDelete(null);
    setSelectedGroupForDelete(null);
    setOpenConfirmDeleteBotDialog(false);
  };

  // Confirm bot deletion
  const handleConfirmDeleteBot = async () => {
    if (selectedBotToDelete && selectedGroupForDelete) {
      try {
        // Optimistically update UI by removing bot from the group
        setOrgGroups((prevGroups) =>
          prevGroups.map((group) =>
            group._id.$oid === selectedGroupForDelete
              ? {
                  ...group,
                  bots: group.bots.filter(
                    (bot) => bot.$oid !== selectedBotToDelete
                  ),
                }
              : group
          )
        );

        // Remove bot from botsData state
        setBotsData((prevBots) => {
          const updatedBots = { ...prevBots };
          delete updatedBots[selectedBotToDelete];
          return updatedBots;
        });

        // Dispatch API call to remove bot
        await dispatch(
          deleteBotFromGroup({
            group_id: selectedGroupForDelete,
            bot_id: selectedBotToDelete,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error removing bot from group:", error);
      }
    }
    handleCloseDeleteBotDialog();
  };

  // functions to add users to group
  function formatToObjectId(value) {
    try {
      if (typeof value === "string") {
        // Extract $oid manually using RegEx to avoid parsing errors
        const match = value.match(/\$oid['"]:\s*['"]([^'"]+)['"]/);
        if (match) {
          return { $oid: match[1] }; // Return as an object
        }
      } else if (typeof value === "object" && value?.$oid) {
        return value; // Already in correct format
      }
      return { $oid: value }; // Fallback for plain string cases
    } catch (error) {
      console.error("Error formatting ObjectId:", error, value);
      return { $oid: null }; // Prevent crashes by returning a valid object
    }
  }

  // Open Add Users Dialog
  const handleOpenUsersDialog = async (groupId, existingUsers) => {
    setSelectedGroupForUsers(groupId); // Store the selected group ID
    if (organization?.users?.length > 1) {
      const data = organization.users.map((usr) => usr["$oid"]);
      setLoadingUsersDialog(true);
      try {
        const response = await dispatch(getAllUsers(data)).unwrap();
        if (response.length > 0) {
          // Convert existingUsers to a Set for efficient lookup
          const existingUsersSet = new Set(existingUsers);
          // Extract and fix `_id` safely without mutating the original object
          const filteredUsers = response
            .map((user) => {
              try {
                return {
                  ...user, // Spread the user object to avoid modifying Redux state
                  _id: formatToObjectId(user._id), // Convert _id safely
                };
              } catch (err) {
                console.error("Error processing user:", err, user);
                return null;
              }
            })
            .filter((user) => user && !existingUsersSet.has(user._id.$oid)); // Remove null users
          setUsersList(filteredUsers);
          setOpenUsersDialog(true);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoadingUsersDialog(false);
      }
    }
  };

  // Handle Checkbox Change
  const handleUserCheckboxChange = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle Adding Users to Group
  const handleAddUsers = async () => {
    if (!selectedGroupForUsers || selectedUsers.length === 0) return;

    setOpenUsersDialog(false);
    // optimistic UI
    const placeholderUsers = selectedUsers.map((id) => ({
      id,
      firstName: "Loading…",
      lastName: "",
      email: "",
      privilege: "",
    }));
    setUsersData((prev) => ({
      ...prev,
      ...Object.fromEntries(placeholderUsers.map((u) => [u.id, u])),
    }));
    setOrgGroups((prev) =>
      prev.map((g) =>
        g._id.$oid === selectedGroupForUsers
          ? {
              ...g,
              users: [...g.users, ...selectedUsers.map((id) => ({ $oid: id }))],
            }
          : g
      )
    );

    try {
      // dispatch bulk‐add
      const response = await dispatch(
        addMultipleUsersToGroup({
          group_id: selectedGroupForUsers,
          user_id_list: selectedUsers,
        })
      ).unwrap();

      // unwrap the per‐user results array
      const results = response.results || [];
      const successfulIds = results
        .filter((r) => r.status === "User added to group successfully")
        .map((r) => r.user_id);

      // fetch full info only for the successful ones
      const usersFull = await Promise.all(
        successfulIds.map((uid) => dispatch(getUserByID(uid)).unwrap())
      );

      // build a map of real user objects
      const updatedUsers = {};
      usersFull.forEach((res) => {
        if (res.user) {
          const u = res.user;
          updatedUsers[u._id.$oid] = {
            id: u._id.$oid,
            firstName: u.first_name,
            lastName: u.last_name,
            email: u.email,
            privilege: u.privilege,
          };
        }
      });

      // patch usersData with the real info
      setUsersData((prev) => ({ ...prev, ...updatedUsers }));

      // ensure orgGroups matches the final state
      setOrgGroups((prev) =>
        prev.map((g) =>
          g._id.$oid === selectedGroupForUsers
            ? {
                ...g,
                users: [
                  ...g.users.filter((u) => !selectedUsers.includes(u.$oid)),
                  ...successfulIds.map((id) => ({ $oid: id })),
                ],
              }
            : g
        )
      );

      // reset dialog & selections
      setSelectedUsers([]);
      setSelectedGroupForUsers(null);
      setOpenUsersDialog(false);
    } catch (err) {
      console.error("Error adding users to group:", err);
    }
  };

  // functions to delete user from grp
  // Open delete user confirmation dialog
  const handleOpenConfirmDeleteUserDialog = (groupId, userId) => {
    setSelectedGroupForUserDelete(groupId);
    setSelectedUserToDelete(userId);
    setOpenConfirmDeleteUserDialog(true);
  };

  // Close delete user confirmation dialog
  const handleCloseDeleteUserDialog = () => {
    setSelectedUserToDelete(null);
    setSelectedGroupForUserDelete(null);
    setOpenConfirmDeleteUserDialog(false);
  };

  // Confirm user deletion
  const handleConfirmDeleteUser = async () => {
    if (selectedUserToDelete && selectedGroupForUserDelete) {
      try {
        // Optimistically update UI by removing user from the group
        setOrgGroups((prevGroups) =>
          prevGroups.map((group) =>
            group._id.$oid === selectedGroupForUserDelete
              ? {
                  ...group,
                  users: group.users.filter(
                    (user) => user.$oid !== selectedUserToDelete
                  ),
                }
              : group
          )
        );

        // Remove user from usersData state
        setUsersData((prevUsers) => {
          const updatedUsers = { ...prevUsers };
          delete updatedUsers[selectedUserToDelete];
          return updatedUsers;
        });

        // Dispatch API call to remove user
        await dispatch(
          deleteUserFromGroup({
            group_id: selectedGroupForUserDelete,
            user_id: selectedUserToDelete,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error removing user from group:", error);
      }
    }
    handleCloseDeleteUserDialog();
  };

  return (
    <Box sx={mainContainer}>
      {user?.user?.privilege === "owner" && (
        <Tooltip title="Create New Group">
          <Button
            variant="contained"
            onClick={handleOpen}
            sx={{
              ...primaryButtonStyles,
              alignSelf: "flex-end",
              mx: 2.5,
              mb: 2,
              width: "auto",
            }}
          >
            <Add />
          </Button>
        </Tooltip>
      )}
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress sx={{ color: theme.textColors.dark.primary }} />
        </Box>
      ) : orgGroups?.length > 0 ? (
        <Box
          sx={{
            width: "100%",
            backgroundColor: theme.backgroundColors.dark.tertiary,
            padding: "20px",
            overflowY: "auto",
            overflowX: "hidden",
            height: "80vh",
            ...scrollbarStyles,
          }}
        >
          {orgGroups.map((group) => (
            <Accordion
              key={group._id.$oid}
              expanded={expandedGroup === group._id.$oid}
              onChange={(event) => {
                if (event.target.closest(".delete-button")) return;
                handleAccordionChange(group._id.$oid, group.users, group.bots);
              }}
              sx={accordionStyles.accordionRoot}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMore sx={{ color: theme.textColors.dark.primary }} />
                }
                sx={accordionStyles.accordionSummary}
              >
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography sx={{ flex: 1 }} variant="body2">
                    {group.name}
                  </Typography>

                  <Typography
                    sx={{ flex: 1, textAlign: "center" }}
                    variant="body2"
                  >
                    Users: {group.users.length}
                  </Typography>
                  <Typography
                    sx={{ flex: 1, textAlign: "center" }}
                    variant="body2"
                  >
                    Bots: {group.bots.length}
                  </Typography>
                  {group.name !== "Default" && group.name !== "Public" && (
                    <IconButton
                      onClick={() =>
                        handleOpenConfirmDeleteGroupDialog(group._id.$oid)
                      }
                      sx={deleteIconStyles}
                      className="delete-button"
                    >
                      <DeleteOutline sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={accordionStyles.accordionDetails}>
                <Box display="flex" gap={2}>
                  {/* Users Table */}
                  <Box flex={1} sx={accordionStyles.accordionDetailCards}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography variant="h6">Users</Typography>
                      <Tooltip title="Add New User to Group">
                        <IconButton
                          onClick={() =>
                            handleOpenUsersDialog(
                              group._id.$oid,
                              group.users.map((user) => user.$oid)
                            )
                          }
                          sx={iconButtonActive}
                        >
                          <Add />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <TableContainer
                      sx={{ ...tableStyles.tableContainer, maxHeight: 300 }}
                    >
                      <Table stickyHeader>
                        <TableHead sx={tableStyles.tableHead}>
                          <TableRow>
                            <TableCell sx={tableStyles.tableHeaderCell}>
                              Username
                            </TableCell>
                            <TableCell sx={tableStyles.tableHeaderCell}>
                              Privilege
                            </TableCell>
                            <TableCell sx={tableStyles.tableHeaderCell}>
                              Email
                            </TableCell>
                            <TableCell
                              sx={tableStyles.tableHeaderCell}
                              align="right"
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loadingUsers ? (
                            <TableRow
                              sx={{
                                ...tableStyles.tableRow,
                                "&:last-child td": { borderBottom: "none" },
                              }}
                            >
                              <TableCell
                                colSpan={4}
                                align="center"
                                sx={tableStyles.tableCell}
                              >
                                <CircularProgress size={24} color="inherit" />
                              </TableCell>
                            </TableRow>
                          ) : group.users.length > 0 ? (
                            group.users.map((userId) => {
                              const user = usersData[userId?.$oid]; // Get user object
                              return user ? (
                                <TableRow
                                  key={userId?.$oid}
                                  sx={{
                                    ...tableStyles.tableRow,
                                    "&:last-child td": { borderBottom: "none" },
                                  }}
                                >
                                  <TableCell sx={tableStyles.tableCell}>
                                    {user.firstName} {user.lastName}{" "}
                                  </TableCell>
                                  <TableCell sx={tableStyles.tableCell}>
                                    {user.privilege}
                                  </TableCell>
                                  <TableCell sx={tableStyles.tableCell}>
                                    {user.email}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={tableStyles.tableCell}
                                  >
                                    {user?.privilege !== "owner" && (
                                      <IconButton
                                        sx={deleteIconStyles}
                                        onClick={() =>
                                          handleOpenConfirmDeleteUserDialog(
                                            group._id.$oid,
                                            userId?.$oid
                                          )
                                        }
                                      >
                                        <DeleteOutline sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ) : null;
                            })
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={3}
                                align="center"
                                sx={tableStyles.tableCell}
                              >
                                No users found.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Bots Table */}
                  <Box flex={1} sx={accordionStyles.accordionDetailCards}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography variant="h6">Bots</Typography>
                      <Tooltip title="Add New Bot to Group">
                        {" "}
                        <IconButton
                          onClick={() =>
                            handleOpenBotsDialog(
                              group._id.$oid,
                              group.bots.map((bot) => bot.$oid)
                            )
                          }
                          sx={iconButtonActive}
                        >
                          <Add />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <TableContainer
                      sx={{ ...tableStyles.tableContainer, maxHeight: 300 }}
                    >
                      <Table stickyHeader>
                        <TableHead sx={tableStyles.tableHead}>
                          <TableRow>
                            <TableCell sx={tableStyles.tableHeaderCell}>
                              Bot Name
                            </TableCell>
                            <TableCell
                              sx={tableStyles.tableHeaderCell}
                              align="right"
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loadingBots ? (
                            <TableRow
                              sx={{
                                ...tableStyles.tableRow,
                                "&:last-child td": { borderBottom: "none" },
                              }}
                            >
                              <TableCell
                                colSpan={2}
                                align="center"
                                sx={tableStyles.tableCell}
                              >
                                <CircularProgress size={24} color="inherit" />
                              </TableCell>
                            </TableRow>
                          ) : group.bots.length > 0 ? (
                            group.bots.map((botId) =>
                              botsData[botId?.$oid] ? (
                                <TableRow
                                  key={botId?.$oid}
                                  sx={{
                                    ...tableStyles.tableRow,
                                    "&:last-child td": { borderBottom: "none" },
                                  }}
                                >
                                  <TableCell sx={tableStyles.tableCell}>
                                    {botsData[botId?.$oid].name}
                                  </TableCell>
                                  <TableCell
                                    sx={tableStyles.tableCell}
                                    align="right"
                                  >
                                    <IconButton
                                      sx={deleteIconStyles}
                                      onClick={() =>
                                        handleOpenConfirmDeleteBotDialog(
                                          group._id.$oid,
                                          botId?.$oid
                                        )
                                      }
                                    >
                                      <DeleteOutline sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ) : null
                            )
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={2}
                                align="center"
                                sx={tableStyles.tableCell}
                              >
                                No bots found.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="100%"
          sx={{ padding: "20px", textAlign: "center" }}
        >
          <Typography variant="h6" sx={{ color: colors.extremeWhite, mb: 2 }}>
            No groups found.
          </Typography>
        </Box>
      )}

      {/* Create Group Dialog */}
      <CreateGroupDialog
        open={open}
        onClose={handleClose}
        groupName={groupName}
        groupNameError={groupNameError}
        onGroupNameChange={handleGroupNameChange}
        onCreateGroup={handleCreateGroup}
        loadingCreateGroup={loadingCreateGroup}
      />

      {/* Delete Group Confirmation Dialog */}
      <DeleteConfirmDailog
        onOpen={openConfirmDeleteGroupDialog}
        onClose={handleCloseDeleteGroupDialog}
        onConfirm={handleConfirmDeleteGroup}
        type={"group"}
      />

      {/* Add Bots to Group Dialog */}
      <AddBotsToGroup
        openBotsDialog={openBotsDialog}
        setOpenBotsDialog={setOpenBotsDialog}
        botsList={botsList}
        loadingBots={loadingBots}
        selectedBots={selectedBots}
        handleCheckboxChange={handleCheckboxChange}
        handleAddBots={handleAddBots}
      />

      {/* Delete Bot Confirmation Dialog */}
      <DeleteConfirmDailog
        onOpen={openConfirmDeleteBotDialog}
        onClose={handleCloseDeleteBotDialog}
        onConfirm={handleConfirmDeleteBot}
        type={"LLM App"}
      />

      {/* Add Users to Group Dialog */}
      <AddUsersToGroup
        openUsersDialog={openUsersDialog}
        setOpenUsersDialog={setOpenUsersDialog}
        loadingUsersDialog={loadingUsersDialog}
        usersList={usersList}
        handleUserCheckboxChange={handleUserCheckboxChange}
        selectedUsers={selectedUsers}
        handleAddUsers={handleAddUsers}
      />
      {/* Delete User Confirmation Dialog */}
      <DeleteConfirmDailog
        onOpen={openConfirmDeleteUserDialog}
        onClose={handleCloseDeleteUserDialog}
        onConfirm={handleConfirmDeleteUser}
        type={"User"}
      />
    </Box>
  );
};

export default GroupManagement;
